import { useState } from 'react'
import { Download, CreditCard, Trash2, Loader2, Banknote, Building2, Shield, Globe, FileText } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { useToast } from '@hooks/use-toast'
import { billingApi } from '@services/billing.service'
import { Invoice } from '@/types'
import { formatCurrency, cn } from '@/lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'

const statusPillConfig: Record<string, { pill: string; dot: string; label: string }> = {
  paid: {
    pill: 'border border-emerald-600/30 bg-emerald-50/70 text-emerald-700',
    dot: 'bg-emerald-500',
    label: 'Pagado',
  },
  pending: {
    pill: 'border border-amber-500/30 bg-amber-50/70 text-amber-700',
    dot: 'bg-amber-400',
    label: 'Pendiente',
  },
  overdue: {
    pill: 'border border-red-400/30 bg-red-50/70 text-red-600',
    dot: 'bg-red-500',
    label: 'Vencida',
  },
  partially_paid: {
    pill: 'border border-amber-500/30 bg-amber-50/70 text-amber-700',
    dot: 'bg-amber-400',
    label: 'Parcial',
  },
  draft: {
    pill: 'border border-gray-300/50 bg-gray-50 text-gray-500',
    dot: 'bg-gray-400',
    label: 'Borrador',
  },
  sent: {
    pill: 'border border-sky-400/30 bg-sky-50/70 text-sky-600',
    dot: 'bg-sky-400',
    label: 'Enviada',
  },
  cancelled: {
    pill: 'border border-gray-300/40 bg-gray-50 text-gray-400',
    dot: 'bg-gray-300',
    label: 'Cancelada',
  },
}

const paymentMethodConfig: Record<string, { icon: React.ReactNode; label: string }> = {
  cash: { icon: <Banknote className="h-3.5 w-3.5" />, label: 'Efectivo' },
  credit_card: { icon: <CreditCard className="h-3.5 w-3.5" />, label: 'Tarjeta de Crédito' },
  debit_card: { icon: <CreditCard className="h-3.5 w-3.5" />, label: 'Tarjeta de Débito' },
  check: { icon: <FileText className="h-3.5 w-3.5" />, label: 'Cheque' },
  bank_transfer: { icon: <Building2 className="h-3.5 w-3.5" />, label: 'Transferencia Bancaria' },
  insurance: { icon: <Shield className="h-3.5 w-3.5" />, label: 'Seguro' },
  online_payment: { icon: <Globe className="h-3.5 w-3.5" />, label: 'Pago en Línea' },
}

interface Props {
  invoice: Invoice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRecordPayment: (invoice: Invoice) => void
  onChanged?: () => void
}

export function InvoiceDetailDialog({ invoice, open, onOpenChange, onRecordPayment, onChanged }: Props) {
  const { toast } = useToast()
  const { formatDate } = useClinicFormat()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  if (!invoice) return null

  const status = statusPillConfig[invoice.status] || statusPillConfig.pending
  const canPay = Number(invoice.balance_due) > 0 && invoice.status !== 'cancelled'
  const method = invoice.payment_method ? paymentMethodConfig[invoice.payment_method] : null

  const subtotal = Number(invoice.subtotal ?? invoice.total_amount)
  const taxAmount = Number(invoice.tax_amount ?? 0)
  const discountAmount = Number(invoice.discount_amount ?? 0)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const blob = await billingApi.downloadInvoice(invoice.id, 'es')
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Invoice_${invoice.invoice_number}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Error al descargar factura',
        variant: 'destructive',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la factura ${invoice.invoice_number}? Esta acción no se puede deshacer.`))
      return
    setIsDeleting(true)
    try {
      await billingApi.deleteInvoice(invoice.id)
      toast({ title: 'Factura eliminada', description: `Se eliminó la factura ${invoice.invoice_number}.` })
      onChanged?.()
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo eliminar la factura',
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {/* Invoice header: number + patient + status */}
          <div className="flex items-start justify-between gap-4 pr-6">
            <div className="flex items-start gap-3">
              {/* Ticket stub */}
              <div className="shrink-0 bg-ml-surface-container-high/60 rounded-lg px-3 py-2 text-center border border-ml-outline-variant/10">
                <p className="text-[8px] font-bold uppercase tracking-widest text-ml-on-surface-variant/30 mb-0.5">
                  Factura
                </p>
                <p className="font-mono text-xs font-bold text-ml-primary">
                  #{invoice.invoice_number ? invoice.invoice_number.slice(-8) : '———'}
                </p>
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-bold text-ml-primary truncate">
                  {invoice.patient_full_name || `Paciente ${invoice.patient_id?.slice(0, 8)}`}
                </DialogTitle>
                <p className="text-xs text-ml-on-surface-variant/50 mt-0.5 font-mono">
                  {invoice.invoice_number}
                </p>
              </div>
            </div>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-semibold shrink-0',
                status.pill,
              )}
            >
              <span className={cn('w-1.5 h-1.5 rounded-full', status.dot)} />
              {status.label}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Dates + payment method */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase font-bold tracking-widest text-ml-on-surface-variant/40 mb-1">
                Fecha
              </p>
              <p className="text-ml-primary font-medium">{formatDate(invoice.invoice_date)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold tracking-widest text-ml-on-surface-variant/40 mb-1">
                Vencimiento
              </p>
              <p className="text-ml-primary font-medium">{formatDate(invoice.due_date)}</p>
            </div>
            {method && (
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-ml-on-surface-variant/40 mb-1">
                  Método de Pago
                </p>
                <p className="inline-flex items-center gap-1.5 text-ml-on-surface-variant font-medium">
                  <span className="text-ml-on-surface-variant/60">{method.icon}</span>
                  {method.label}
                </p>
              </div>
            )}
          </div>

          {/* Line items */}
          <div>
            <p className="text-[10px] uppercase font-bold tracking-widest text-ml-on-surface-variant/40 mb-2">
              Servicios
            </p>
            {invoice.line_items && invoice.line_items.length > 0 ? (
              <div className="rounded-lg border border-ml-outline-variant/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-ml-surface-container-low text-[10px] uppercase tracking-widest text-ml-on-surface-variant/40">
                      <th className="text-left px-4 py-2.5 font-bold">Descripción</th>
                      <th className="text-center px-3 py-2.5 font-bold w-16">Cant.</th>
                      <th className="text-right px-3 py-2.5 font-bold w-28">P. Unit.</th>
                      <th className="text-right px-4 py-2.5 font-bold w-28">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map((item, idx) => (
                      <tr key={item.id || idx} className="border-t border-ml-outline-variant/10">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-ml-primary">{item.service_description}</span>
                          {item.service_code && (
                            <span className="ml-2 text-[10px] font-mono text-ml-on-surface-variant/40">
                              {item.service_code}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center text-ml-on-surface-variant">
                          {item.quantity}
                        </td>
                        <td className="px-3 py-2.5 text-right text-ml-on-surface-variant">
                          {formatCurrency(Number(item.unit_price))}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-ml-primary">
                          {formatCurrency(Number(item.total_price ?? item.quantity * item.unit_price))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ml-on-surface-variant/50 italic">Sin líneas de detalle.</p>
            )}
          </div>

          {/* Totals */}
          <div className="rounded-lg bg-ml-surface-container-low/50 px-4 py-3 space-y-1.5 text-sm">
            {invoice.subtotal !== undefined && invoice.subtotal !== invoice.total_amount && (
              <div className="flex justify-between text-ml-on-surface-variant/70">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Descuento</span>
                <span>−{formatCurrency(discountAmount)}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="flex justify-between text-ml-on-surface-variant/70">
                <span>Impuestos</span>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-ml-outline-variant/15 pt-1.5">
              <span className="font-bold text-ml-primary">Total</span>
              <span className="font-bold text-ml-primary">{formatCurrency(Number(invoice.total_amount))}</span>
            </div>
            <div className="flex justify-between text-ml-on-surface-variant/70">
              <span>Pagado</span>
              <span className="font-medium text-emerald-700">
                {formatCurrency(Number(invoice.amount_paid))}
              </span>
            </div>
            <div className="flex justify-between text-base font-bold">
              <span className={Number(invoice.balance_due) > 0 ? 'text-amber-700' : 'text-ml-primary'}>
                Saldo Pendiente
              </span>
              <span className={Number(invoice.balance_due) > 0 ? 'text-amber-700' : 'text-ml-primary'}>
                {formatCurrency(Number(invoice.balance_due))}
              </span>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div>
              <p className="text-[10px] uppercase font-bold tracking-widest text-ml-on-surface-variant/40 mb-1.5">
                Notas
              </p>
              <p className="text-sm text-ml-on-surface-variant/70 bg-ml-surface-container-low/50 rounded-lg px-4 py-3">
                {invoice.notes}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            className="text-ml-error hover:text-ml-error hover:bg-ml-error-container/30"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Eliminar
          </Button>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Descargar PDF
            </Button>
            {canPay && (
              <Button
                type="button"
                className="bg-ml-primary text-white hover:bg-ml-primary-container"
                onClick={() => onRecordPayment(invoice)}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Registrar Pago
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
